import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { customToast } from "../../../../components/notify";
import { IApiFestivalSetting, IFestivalFormData } from "../../types";
import { festivalValidationSchema } from "../../validations/validations";
import { INITIAL_FESTIVAL_SETTINGS } from "../configs";
import { mergeKioskSettings } from "../utils/mergeKioskSettings";
import useKioskSettings from "./useKioskSettings";
import { useTabGuard } from "./useTabGuard";

interface UseFestivalSettingsProps {
    characterId: string;
}

const DEFAULT_FESTIVAL_FORM: IFestivalFormData = {
    use_festival: INITIAL_FESTIVAL_SETTINGS.use_festival,
    bgm: INITIAL_FESTIVAL_SETTINGS.setting.bgm,
    bg: INITIAL_FESTIVAL_SETTINGS.setting.bg,
    bg_obj: INITIAL_FESTIVAL_SETTINGS.setting.bg_obj,
    effect: INITIAL_FESTIVAL_SETTINGS.setting.effect,
    character: INITIAL_FESTIVAL_SETTINGS.setting.character,
    character_is_conservative: INITIAL_FESTIVAL_SETTINGS.setting.character_is_conservative,
    filter: INITIAL_FESTIVAL_SETTINGS.setting.filter,
    filter_keep_default: INITIAL_FESTIVAL_SETTINGS.setting.filter_keep_default,
};

export const useFestivalSettings = ({ characterId }: UseFestivalSettingsProps) => {
    const { t } = useTranslation();
    const { kioskSetting, isLoadingKioskSetting, isUpdatingKioskSetting, updateSettings } = useKioskSettings({
        characterId,
    });

    // ======================= Form Setup =======================
    const methods = useForm<IFestivalFormData>({
        defaultValues: DEFAULT_FESTIVAL_FORM,
        resolver: yupResolver(festivalValidationSchema()),
        mode: "onChange",
    });

    const { control, handleSubmit, formState, reset, setValue } = methods;

    const watchedUseFestival = useWatch({ control, name: "use_festival" });

    // ======================= Toggle Handler =======================
    const isEnabled = watchedUseFestival;
    const handleToggle = useCallback(
        (enabled: boolean) => {
            setValue("use_festival", enabled, { shouldDirty: true });
        },
        [setValue]
    );

    // ======================= Data Transformation =======================
    const transformAPItoForm = useCallback((apiData: IApiFestivalSetting | undefined): IFestivalFormData => {
        if (!apiData) {
            return DEFAULT_FESTIVAL_FORM;
        }

        return {
            use_festival: apiData.use_festival,
            bgm: apiData.setting?.bgm ?? false,
            bg: apiData.setting?.bg ?? false,
            bg_obj: apiData.setting?.bg_obj ?? false,
            effect: apiData.setting?.effect ?? false,
            character: apiData.setting?.character ?? false,
            character_is_conservative: apiData.setting?.character_is_conservative ?? false,
            filter: apiData.setting?.filter ?? false,
            filter_keep_default: apiData.setting?.filter_keep_default ?? false,
        };
    }, []);

    const transformFormToAPI = useCallback((formData: IFestivalFormData): IApiFestivalSetting => {
        return {
            use_festival: formData.use_festival,
            setting: {
                bgm: formData.bgm,
                bg: formData.bg,
                bg_obj: formData.bg_obj,
                effect: formData.effect,
                character: formData.character,
                character_is_conservative: formData.character_is_conservative,
                filter: formData.filter,
                filter_keep_default: formData.filter_keep_default,
            },
        };
    }, []);

    // ======================= Submit Handler =======================
    const onSubmit = useCallback(
        async (data: IFestivalFormData) => {
            try {
                const apiPayload = transformFormToAPI(data);
                const currentNative = kioskSetting?.data?.native;
                const mergedNative = mergeKioskSettings(currentNative, { festival_setting: apiPayload });

                await updateSettings({ native: mergedNative });

                // 重置表單，清除髒值狀態
                reset(data);
                customToast.success(t("COMMON.SETTINGS_SAVED") || "設定已儲存");
            } catch (error) {
                customToast.error(t("COMMON.SETTINGS_ERROR") || "儲存設定失敗");
            }
        },
        [transformFormToAPI, kioskSetting, updateSettings, reset, t]
    );

    const saveHandler = useCallback(async () => {
        await handleSubmit(onSubmit)();
    }, [handleSubmit, onSubmit]);

    useTabGuard("festival", formState.isDirty, saveHandler);

    // ======================= Initialize Form from API =======================
    useEffect(() => {
        // 只有當載入完成時才重置表單，避免競態條件
        if (!isLoadingKioskSetting) {
            const festivalData = kioskSetting?.data?.native?.festival_setting;
            const formData = transformAPItoForm(festivalData);
            reset(formData);
        }
    }, [kioskSetting?.data?.native?.festival_setting, isLoadingKioskSetting, reset, transformAPItoForm]);

    return {
        // Form methods
        methods,
        handleSubmit,
        onSubmit,
        formState,

        // Toggle state
        isEnabled,
        handleToggle,

        // State
        watchedUseFestival,

        // Loading states
        isLoadingKioskSetting,
        isUpdatingKioskSetting,
    };
};

export default useFestivalSettings;
